import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { track } from '../../analytics';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';
import 'leaflet/dist/leaflet.css';
import { useUsers } from '../../hooks/useUsers';
import { useUser } from '../providers/UserProvider';
import { usePins } from '../../hooks/usePins';
import { useEvents } from '../../hooks/useEvents';
import { StabeonUser, DEFAULT_AVATAR } from '../../types/user';
import { Pin } from '../../types/pin';
import { StabeonEvent, getEventStatus } from '../../types/event';
import { ACTIVITY_CATEGORIES } from '../../types/user';
import { createAvatarIcon } from './AvatarMarkerIcon';
import { createPinIcon } from './PinMarkerIcon';
import { createEventIcon } from './EventMarkerIcon';
import { MapFilters } from './MapFilterToggle';
import { CapsulePin } from '../../types/capsule';
import { CapsulePinMarker } from '../capsules/CapsulePinMarker';

interface Props {
  onSelectUser: (u: StabeonUser) => void;
  onSelectPin: (p: Pin) => void;
  onSelectEvent: (e: StabeonEvent) => void;
  onCreatePin: (lat: number, lng: number) => void;
  visibilityRadius: number | null;
  filters?: MapFilters;
  flyTo?: { lat: number; lng: number } | null;
  onFlyToDone?: () => void;
  lightMode?: boolean;
  /** Guest/preview location — shows a simple "you are here" pin when user is not logged in */
  guestLocation?: { lat: number; lng: number } | null;
  /** Time capsule pins — only visible to their creator */
  capsules?: CapsulePin[];
}

// Calls map.invalidateSize() whenever the container goes from hidden → visible.
// This is the primary fix for the "white map after tab switch" bug.
function MapResizeWatcher() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    const ro = new ResizeObserver(() => {
      if (container.offsetWidth > 0 && container.offsetHeight > 0) {
        map.invalidateSize({ animate: false, pan: false });
      }
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

function LocationTracker({ user }: { user: StabeonUser }) {
  const map = useMap();
  const centred = useRef(false);
  useEffect(() => {
    if (!user.lat || !user.lng) return;
    if (!centred.current) {
      // First fix — fly straight there (no animation so it's instant)
      map.setView([user.lat, user.lng], 14, { animate: false });
      centred.current = true;
    }
  }, [user.lat, user.lng, map]);
  return null;
}

function GuestLocationTracker({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  const centred = useRef(false);
  useEffect(() => {
    if (!lat || !lng) return;
    if (!centred.current) {
      map.setView([lat, lng], 13, { animate: false });
      centred.current = true;
    }
  }, [lat, lng, map]);
  return null;
}

function FlyToHandler({ target, onDone }: { target: { lat: number; lng: number } | null; onDone?: () => void }) {
  const map = useMap();
  useEffect(() => {
    if (target) {
      map.flyTo([target.lat, target.lng], 15, { duration: 1.5 });
      onDone?.();
    }
  }, [target, map, onDone]);
  return null;
}

// Pixels the pointer must travel before the long-press is cancelled.
// Finger tremor on mobile is typically 2-5px — 12px gives comfortable margin.
const LONG_PRESS_MOVE_THRESHOLD = 12;
const LONG_PRESS_DELAY_MS = 600;

function LongPressHandler({ onLongPress }: { onLongPress: (lat: number, lng: number) => void }) {
  const map = useMap();

  // Store the callback in a ref so the effect below never needs to re-run
  // when the parent re-renders with a new function identity.  A re-running
  // effect would call cleanup() mid-hold and kill an active long-press.
  const onLongPressRef = useRef(onLongPress);
  useEffect(() => { onLongPressRef.current = onLongPress; }, [onLongPress]);

  useEffect(() => {
    const container = map.getContainer();

    // ── Ripple overlay ───────────────────────────────────────────────────────
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:absolute;inset:0;pointer-events:none;z-index:500;overflow:hidden;';
    container.appendChild(overlay);

    const showRipple = (x: number, y: number) => {
      overlay.innerHTML = `
        <div style="position:absolute;left:${x}px;top:${y}px;transform:translate(-50%,-50%)">
          <div style="width:56px;height:56px;border-radius:50%;border:2.5px solid rgba(124,58,237,0.9);animation:longpress-ripple 0.6s ease-out forwards"></div>
          <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:8px;height:8px;border-radius:50%;background:#7c3aed;opacity:0.9"></div>
        </div>`;
    };
    const hideRipple = () => { overlay.innerHTML = ''; };

    // ── State (local vars — no refs needed, effect runs once) ────────────────
    let timerId: ReturnType<typeof setTimeout> | null = null;
    let pressing = false;
    let startClientX = 0, startClientY = 0;
    let startContainerX = 0, startContainerY = 0;

    const cancel = () => {
      pressing = false;
      hideRipple();
      if (timerId) { clearTimeout(timerId); timerId = null; }
    };

    const beginPress = (clientX: number, clientY: number) => {
      cancel();
      const rect = container.getBoundingClientRect();
      startClientX = clientX; startClientY = clientY;
      startContainerX = clientX - rect.left;
      startContainerY = clientY - rect.top;
      pressing = true;
      showRipple(startContainerX, startContainerY);
      timerId = setTimeout(() => {
        if (!pressing) return;
        pressing = false;
        hideRipple();
        // Use Leaflet's containerPointToLatLng to convert pixel → world coords
        const latlng = map.containerPointToLatLng([startContainerX, startContainerY]);
        track('map_longpress', {});
        onLongPressRef.current(latlng.lat, latlng.lng);
      }, LONG_PRESS_DELAY_MS);
    };

    const checkMove = (clientX: number, clientY: number) => {
      if (!pressing) return;
      const dx = clientX - startClientX;
      const dy = clientY - startClientY;
      if (Math.sqrt(dx * dx + dy * dy) > LONG_PRESS_MOVE_THRESHOLD) cancel();
    };

    // ── Native DOM listeners (bypasses Leaflet's event routing entirely) ─────
    // Leaflet's own touch/mouse handling (drag, click) does not interfere with
    // native listeners added directly to the container element.
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { cancel(); return; }
      const t = e.touches[0];
      beginPress(t.clientX, t.clientY);
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      checkMove(e.touches[0].clientX, e.touches[0].clientY);
    };
    const onTouchEnd    = () => cancel();
    const onTouchCancel = () => cancel(); // iOS fires this on system interrupts

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      beginPress(e.clientX, e.clientY);
    };
    const onMouseMove = (e: MouseEvent) => checkMove(e.clientX, e.clientY);
    const onMouseUp   = () => cancel();

    container.addEventListener('touchstart',  onTouchStart,  { passive: true });
    container.addEventListener('touchmove',   onTouchMove,   { passive: true });
    container.addEventListener('touchend',    onTouchEnd,    { passive: true });
    container.addEventListener('touchcancel', onTouchCancel, { passive: true });
    container.addEventListener('mousedown',   onMouseDown);
    container.addEventListener('mousemove',   onMouseMove);
    container.addEventListener('mouseup',     onMouseUp);

    return () => {
      container.removeEventListener('touchstart',  onTouchStart);
      container.removeEventListener('touchmove',   onTouchMove);
      container.removeEventListener('touchend',    onTouchEnd);
      container.removeEventListener('touchcancel', onTouchCancel);
      container.removeEventListener('mousedown',   onMouseDown);
      container.removeEventListener('mousemove',   onMouseMove);
      container.removeEventListener('mouseup',     onMouseUp);
      if (timerId) clearTimeout(timerId);
      if (container.contains(overlay)) container.removeChild(overlay);
    };
  }, [map]); // ← onLongPress intentionally NOT here; accessed via ref above

  return null;
}

function MapEventTracker() {
  const lastZoom = useRef<number | null>(null);
  useMapEvents({
    click() {
      track('map_tap', {});
    },
    zoomend(e) {
      const z = (e.target as any).getZoom?.() ?? null;
      if (z !== null && z !== lastZoom.current) {
        track('map_zoom', { zoom_level: z });
        lastZoom.current = z;
      }
    },
  });
  return null;
}

export function MapView({ onSelectUser, onSelectPin, onSelectEvent, onCreatePin, visibilityRadius, filters, flyTo, onFlyToDone, lightMode, guestLocation, capsules }: Props) {
  const showUsers = filters?.showUsers ?? true;
  const showPins = filters?.showPins ?? true;
  const showEvents = filters?.showEvents ?? true;
  const allUsers = useUsers();
  const { user: me } = useUser();
  const { pins } = usePins();
  const { events } = useEvents();

  // Other users (excluding me) filtered by visibility radius
  const otherUsers = useMemo(() => {
    const others = allUsers.filter(u => u.nullifierHash !== me?.nullifierHash);
    if (!visibilityRadius || !me?.lat || !me?.lng) return others;
    return others.filter(u => {
      if (!u.lat || !u.lng) return false;
      const dlat = u.lat - me.lat!;
      const dlng = u.lng - me.lng!;
      const km = Math.sqrt(dlat * dlat + dlng * dlng) * 111;
      return km <= visibilityRadius;
    });
  }, [allUsers, visibilityRadius, me]);

  const handleLongPress = useCallback((lat: number, lng: number) => {
    onCreatePin(lat, lng);
  }, [onCreatePin]);

  const RECENT_THRESHOLD = 5 * 60 * 1000;

  return (
    <MapContainer
      center={[20, 0]}
      zoom={2}
      style={{ height: '100%', width: '100%' }}
      zoomControl={false}
    >
      <TileLayer
        key={lightMode ? 'light' : 'dark'}
        attribution='&copy; <a href="https://www.openstreetmap.org">OpenStreetMap</a> &copy; <a href="https://carto.com">CARTO</a>'
        url={lightMode
          ? 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png'
          : 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'
        }
      />
      <MapResizeWatcher />
      <MapEventTracker />
      {me && <LocationTracker user={me} />}
      {!me && guestLocation && <GuestLocationTracker lat={guestLocation.lat} lng={guestLocation.lng} />}
      <FlyToHandler target={flyTo || null} onDone={onFlyToDone} />
      <LongPressHandler onLongPress={handleLongPress} />

      {/* ALWAYS render current user marker when they have location */}
      {showUsers && me && me.lat && me.lng && (
        <Marker
          key={`me_${me.avatar?.profilePhoto ? me.avatar.profilePhoto.slice(-12) : me.avatar?.emoji ?? 'x'}`}
          position={[me.lat, me.lng]}
          icon={createAvatarIcon(me.avatar || DEFAULT_AVATAR, me.isMoving, true)}
          eventHandlers={{ click: () => { track('marker_tapped', { marker_type: 'user' }); onSelectUser(me); } }}
        />
      )}

      {/* Guest/preview mode: show "you are here" marker */}
      {showUsers && !me && guestLocation && (
        <Marker
          key="guest"
          position={[guestLocation.lat, guestLocation.lng]}
          icon={createAvatarIcon(DEFAULT_AVATAR, false, true)}
        />
      )}

      {/* Other user markers */}
      {showUsers && (
        <MarkerClusterGroup chunkedLoading maxClusterRadius={40} showCoverageOnHover={false}>
          {otherUsers.map(u => {
            if (!u.lat || !u.lng) return null;
            const avatar = u.avatar || DEFAULT_AVATAR;
            const icon = createAvatarIcon(avatar, u.isMoving, false);
            return (
              <Marker
                key={u.nullifierHash}
                position={[u.lat, u.lng]}
                icon={icon}
                eventHandlers={{ click: () => { track('marker_tapped', { marker_type: 'user' }); onSelectUser(u); } }}
              />
            );
          })}
        </MarkerClusterGroup>
      )}

      {/* Pin markers */}
      {showPins && (
        <MarkerClusterGroup chunkedLoading maxClusterRadius={50} showCoverageOnHover={false}>
          {pins.map(pin => {
            if (!pin.lat || !pin.lng) return null;
            const catInfo = ACTIVITY_CATEGORIES.find(c => c.key === pin.category);
            const isRecent = (Date.now() - pin.createdAt) < RECENT_THRESHOLD;
            const icon = createPinIcon(catInfo?.emoji || '📍', pin.commentCount, isRecent, pin.markerImage);
            return (
              <Marker
                key={`pin_${pin.id}`}
                position={[pin.lat, pin.lng]}
                icon={icon}
                eventHandlers={{ click: () => { track('marker_tapped', { marker_type: 'pin' }); onSelectPin(pin); } }}
              />
            );
          })}
        </MarkerClusterGroup>
      )}

      {/* Event markers */}
      {showEvents && (
        <MarkerClusterGroup chunkedLoading maxClusterRadius={60} showCoverageOnHover={false}>
          {events.map(evt => {
            if (!evt.lat || !evt.lng) return null;
            const catInfo = ACTIVITY_CATEGORIES.find(c => c.key === evt.category);
            const status = getEventStatus(evt);
            const icon = createEventIcon(catInfo?.emoji || '🎉', evt.attendeeCount, status === 'happening');
            return (
              <Marker
                key={`evt_${evt.id}`}
                position={[evt.lat, evt.lng]}
                icon={icon}
                eventHandlers={{ click: () => { track('marker_tapped', { marker_type: 'event' }); onSelectEvent(evt); } }}
              />
            );
          })}
        </MarkerClusterGroup>
      )}

      {/* Capsule pin markers — only visible to creator (filtered in App.tsx before passing down) */}
      {capsules && capsules.filter(c => c.lat && c.lng).map(capsule => (
        <CapsulePinMarker key={`capsule_${capsule.id}`} capsule={capsule} />
      ))}
    </MapContainer>
  );
}
